// ScoreSparkline.js — tiny year-by-year frequency chart for a single
// scoreline. In single-season mode, uses the in-memory match list. In
// all-seasons mode, lazy-fetches via the /scores?score=X-Y endpoint, which
// is keyed winner-first; home-away cells filter the result by orientation.

import React, { useEffect, useMemo, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BRAND } from "../../theme";
import { HOME_AWAY, seasonStartYear, toWinnerFirst } from "./utils";
import { API_URL } from "../../api";


function buildSeries(matches) {
  const byYear = new Map();
  for (const m of matches) {
    const y = seasonStartYear(m.utcDate);
    if (y == null) continue;
    byYear.set(y, (byYear.get(y) || 0) + 1);
  }
  if (!byYear.size) return [];
  const years = [...byYear.keys()];
  const min = Math.min(...years);
  const max = Math.max(...years);
  const out = [];
  for (let y = min; y <= max; y++) {
    out.push({
      year: y,
      count: byYear.get(y) || 0,
      label: `${y % 100}/${(y + 1) % 100}`.padStart(5, "0"),
    });
  }
  return out;
}

export default function ScoreSparkline({ selectedScore, axes, seasonMatches }) {
  // seasonMatches: array from scoreMap[key] in single-season mode (or null)
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedScore) return;
    if (seasonMatches && seasonMatches.length) {
      setFetched(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFetched(null);
    fetch(`${API_URL}?score=${encodeURIComponent(toWinnerFirst(selectedScore))}&limit=5000`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        const items = d?.items || [];
        const inCell =
          axes === HOME_AWAY
            ? items.filter((m) => `${m.homeTeamScore}-${m.awayTeamScore}` === selectedScore)
            : items;
        if (!cancelled) setFetched(inCell);
      })
      .catch(() => {
        if (!cancelled) setFetched([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedScore, axes, seasonMatches]);

  const series = useMemo(() => {
    if (seasonMatches && seasonMatches.length) return buildSeries(seasonMatches);
    if (fetched) return buildSeries(fetched);
    return [];
  }, [seasonMatches, fetched]);

  const peak = useMemo(
    () => series.reduce((m, d) => Math.max(m, d.count), 0),
    [series]
  );

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="overline"
          sx={{
            color: BRAND.violet,
            fontWeight: 700,
            letterSpacing: "0.1em",
          }}
        >
          Occurrences by Season
        </Typography>
        <Skeleton
          variant="rectangular"
          height={80}
          sx={{ bgcolor: "rgba(255,255,255,0.05)", borderRadius: 1, mt: 0.5 }}
        />
      </Box>
    );
  }

  if (!series.length) return null;

  const multiSeason = series.length > 1;

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: BRAND.violet,
            fontWeight: 700,
            letterSpacing: "0.1em",
          }}
        >
          Occurrences by Season
        </Typography>
        {multiSeason && (
          <Typography
            variant="caption"
            sx={{ color: BRAND.textSecondary, fontSize: 10 }}
          >
            {series[0].year}–{series[series.length - 1].year}
          </Typography>
        )}
      </Box>
      <Box sx={{ height: 70, mt: 0.5 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={series}
            margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
            barCategoryGap={1}
          >
            <defs>
              <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={BRAND.cyan} stopOpacity={0.95} />
                <stop offset="100%" stopColor={BRAND.violet} stopOpacity={0.7} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="year"
              tick={{ fontSize: 9, fill: BRAND.textSecondary }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis hide domain={[0, Math.max(peak, 1)]} />
            <Tooltip
              cursor={{ fill: "rgba(111,93,245,0.08)" }}
              contentStyle={{
                background: "rgba(15,15,19,0.95)",
                border: `1px solid ${BRAND.divider}`,
                borderRadius: 8,
                fontSize: 12,
                padding: "6px 10px",
              }}
              labelStyle={{ color: BRAND.cyan, fontWeight: 700 }}
              itemStyle={{ color: "#fff" }}
              formatter={(v) => [`${v} match${v === 1 ? "" : "es"}`, ""]}
              labelFormatter={(y) => `${y}/${(y + 1).toString().slice(-2)}`}
              separator=""
            />
            <Bar
              dataKey="count"
              fill="url(#sparkGrad)"
              radius={[3, 3, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
